import { computed, unref } from 'vue'
import moment from 'moment'
import store from '@/store'
import i18n from '@/i18n'
import { ATTRIBUTE_TYPE } from 'ifield-core-components'
import {
  FUNCTION_TYPE,
  DATE_FORMAT_SLASH,
  TIME_FORMAT_SECOND,
} from '@/constants'
import {
  useUserStatusAdditionalItemAccuracy,
  useUserStatusAdditionalItemActivity,
  useUserStatusAdditionalItemBattery,
  useUserStatusAdditionalItemIndoor,
  useUserStatusAdditionalItemPlace,
  useUserStatusAdditionalItemPositioning,
  useUserStatusAdditionalItemStatus,
  useUserStatusAdditionalItemUserAttribute,
} from '@/composables/user/additionalItem'
import { useDateDisplayText } from '@/composables/UseDateDisplayText'
import {
  compareString,
  compareNumber,
  compareDatetime,
  compareRole,
  compareActivity,
  compareAccuracyRating,
  attributeComparator,
} from './UserListRowSortHelper'

/**
 * カラムID
 */
export const COLUMN_ID = {
  ID: 'id',
  USER_NAME: 'user_name',
  ORGANIZATION: 'organization',
  INDOOR: 'indoor',
  PLACE: 'place',
  CHECKIN: 'checkin',
  STATUS: 'status',
  ACTIVITY: 'activity',
  POSITIONING: 'positioning',
  ACCURACY: 'accuracy',
  ACCURACY_RATING: 'accuracyRating',
  BATTERY: 'battery',
  LAST_POSITIONING_AT: 'last_positioning_at',
  USER_NAME_KANA: 'userNameKana',
  ROLE: 'role',
  MANAGER: 'manager',
  TEL: 'tel',
  EMAIL: 'email',
  REMARKS: 'remarks',
  LAST_LOGIN_AT: 'lastLoginAt',
  CREATE_USER_NAME: 'createUserName',
  CREATE_AT: 'createAt',
  UPDATE_USER_NAME: 'updateUserName',
  UPDATE_AT: 'updateAt',
}

/**
 * カラム種類
 */
export const COLUMN_TYPE = {
  STANDARD: 'standard', // 基本情報
  POSITIONING: 'positioning', // 測位情報
}

/**
 * カラムIDとカラム種類の対応
 */
export const COLUMN_ID_TYPE_RELATION = {
  [COLUMN_ID.ID]: COLUMN_TYPE.STANDARD,
  [COLUMN_ID.USER_NAME]: COLUMN_TYPE.STANDARD,
  [COLUMN_ID.ORGANIZATION]: COLUMN_TYPE.STANDARD,
  [COLUMN_ID.INDOOR]: COLUMN_TYPE.POSITIONING,
  [COLUMN_ID.PLACE]: COLUMN_TYPE.POSITIONING,
  [COLUMN_ID.CHECKIN]: COLUMN_TYPE.POSITIONING,
  [COLUMN_ID.STATUS]: COLUMN_TYPE.POSITIONING,
  [COLUMN_ID.ACTIVITY]: COLUMN_TYPE.POSITIONING,
  [COLUMN_ID.POSITIONING]: COLUMN_TYPE.POSITIONING,
  [COLUMN_ID.ACCURACY]: COLUMN_TYPE.POSITIONING,
  [COLUMN_ID.ACCURACY_RATING]: COLUMN_TYPE.POSITIONING,
  [COLUMN_ID.BATTERY]: COLUMN_TYPE.POSITIONING,
  [COLUMN_ID.LAST_POSITIONING_AT]: COLUMN_TYPE.POSITIONING,
  [COLUMN_ID.USER_NAME_KANA]: COLUMN_TYPE.STANDARD,
  [COLUMN_ID.ROLE]: COLUMN_TYPE.STANDARD,
  [COLUMN_ID.MANAGER]: COLUMN_TYPE.STANDARD,
  [COLUMN_ID.TEL]: COLUMN_TYPE.STANDARD,
  [COLUMN_ID.EMAIL]: COLUMN_TYPE.STANDARD,
  [COLUMN_ID.REMARKS]: COLUMN_TYPE.STANDARD,
  [COLUMN_ID.LAST_LOGIN_AT]: COLUMN_TYPE.STANDARD,
  [COLUMN_ID.CREATE_USER_NAME]: COLUMN_TYPE.STANDARD,
  [COLUMN_ID.CREATE_AT]: COLUMN_TYPE.STANDARD,
  [COLUMN_ID.UPDATE_USER_NAME]: COLUMN_TYPE.STANDARD,
  [COLUMN_ID.UPDATE_AT]: COLUMN_TYPE.STANDARD,
}

/**
 * カラムIDと必要な機能権限の対応
 * 存在しないIDは機能権限に関わらず表示可能
 */
export const COLUMN_ID_FUNCTION_TYPE_RELATION = {
  [COLUMN_ID.INDOOR]: [FUNCTION_TYPE.GPS, FUNCTION_TYPE.INDOOR],
  [COLUMN_ID.PLACE]: [FUNCTION_TYPE.GPS, FUNCTION_TYPE.PLACE],
  [COLUMN_ID.CHECKIN]: [
    FUNCTION_TYPE.GPS,
    FUNCTION_TYPE.PLACE,
    FUNCTION_TYPE.CHECKIN,
  ],
  [COLUMN_ID.STATUS]: [FUNCTION_TYPE.STATUS],
  [COLUMN_ID.ACTIVITY]: [FUNCTION_TYPE.GPS],
  [COLUMN_ID.POSITIONING]: [FUNCTION_TYPE.GPS],
  [COLUMN_ID.ACCURACY]: [FUNCTION_TYPE.GPS],
  [COLUMN_ID.ACCURACY_RATING]: [FUNCTION_TYPE.GPS],
  [COLUMN_ID.BATTERY]: [FUNCTION_TYPE.GPS],
  [COLUMN_ID.LAST_POSITIONING_AT]: [FUNCTION_TYPE.GPS],
}

/**
 * レコード操作日時の表示フォーマット
 */
const RECORD_DATE_FORMAT = `${DATE_FORMAT_SLASH} ${TIME_FORMAT_SECOND}`

/**
 * 追加データタイプ定義
 * @type {Object}
 */
export const DATA_TYPE_DEFINITIONS = {
  // 権限
  role: {
    baseDataType: 'number',
    extendsDataType: 'number',
  },
  // ユーザー属性の数値
  attributeNumber: {
    baseDataType: 'object',
    extendsDataType: 'object',
  },
}

/**
 * ユーザー属性種類のセルデータ種類
 */
const ATTRIBUTE_CELL_DATA_TYPE = {
  [ATTRIBUTE_TYPE.STRING]: 'text',
  [ATTRIBUTE_TYPE.NUMBER]: 'attributeNumber',
  [ATTRIBUTE_TYPE.SINGLE_SELECT]: 'text',
  [ATTRIBUTE_TYPE.DATE]: 'dateString',
  [ATTRIBUTE_TYPE.DATETIME]: 'dateString',
  [ATTRIBUTE_TYPE.CHECK]: 'text',
  [ATTRIBUTE_TYPE.TOGGLE]: 'text',
  [ATTRIBUTE_TYPE.LABEL]: 'text',
  [ATTRIBUTE_TYPE.LINK]: 'text',
}

/**
 * デフォルトのカラム定義
 * @type {ColDef}
 */
export const DEFAULT_COLUMN_DEF = {
  initialWidth: 200,
  minWidth: 48,
  valueFormatter: ({ value }) => (value == null || value === '' ? '-' : value),
}

/**
 * 所属の連結テキストを取得する
 * @param {Object[]} groups グループデータ
 * @param {string} [separator=', '] 連結時のセパレータ
 * @returns {string}
 */
const getOrganizationText = (groups, separator = ', ') => {
  if (!groups?.length) return ''

  const defaultOrganization =
    groups.find((g) => g.default_organization) || groups[0]
  const otherOrganizations = groups.filter(
    (g) => g.id !== defaultOrganization.id
  )
  return [defaultOrganization, ...otherOrganizations]
    .map((o) => o.organization_name)
    .join(separator)
}

/**
 * 初回データ表示時に幅をコンテンツにfitさせるカラム
 */
const AUTO_WIDTH_COLUMNS_ON_FIRST_RENDERED = [
  COLUMN_ID.ID,
  COLUMN_ID.CHECKIN,
  COLUMN_ID.ACTIVITY,
  COLUMN_ID.POSITIONING,
  COLUMN_ID.BATTERY,
]

/**
 * ユーザーリストとして表示するカラム定義の利用
 * @param {RefLike.<string|Date>} updateDate データ更新日時
 */
export const useUserListColumnDefinition = ({ updateDate }) => {
  /**
   * ベースとなる時刻
   * @type {ComputedRef.<Moment>}
   */
  const baseDate = computed(() => {
    const mUpdateDate = moment(unref(updateDate))
    return mUpdateDate.isValid() ? mUpdateDate : moment()
  })

  /**
   * 測位中と判定する閾値時刻
   * 最終測位日時が当値より後の場合は測位中となる
   * @type {ComputedRef.<Moment>}
   */
  const positioningThresholdDate = computed(() =>
    moment(baseDate.value).subtract(
      store.state.userList.positioningThreshold,
      'minutes'
    )
  )

  /**
   * 当 composable のメンバーが使用する grid context データ
   * @type {ComputedRef.<Object>}
   */
  const context = computed(() => ({
    updateDate: baseDate.value,
    positioningThresholdDate: positioningThresholdDate.value,
  }))

  /**
   * 固定で表示するカラムの定義
   * @type {ComputedRef.<ColDef[]>}
   */
  const fixedColumnDefs = computed(() => [
    // ID
    {
      colId: COLUMN_ID.ID,
      headerName: i18n.t('useUserListColumnDefinition.field.id'),
      cellDataType: 'number',
      field: 'id',
      pinned: 'left',
      type: 'numericColumn',
      cellRenderer: 'TextWithIFieldTooltipCellRenderer',
      initialWidth: 72,
    },
    // 名前
    {
      colId: COLUMN_ID.USER_NAME,
      headerName: i18n.t('useUserListColumnDefinition.field.userName'),
      cellDataType: 'text',
      field: 'user_name',
      pinned: 'left',
      cellRenderer: 'TextWithIFieldTooltipCellRenderer',
    },
    // 所属
    {
      colId: COLUMN_ID.ORGANIZATION,
      headerName: i18n.t('useUserListColumnDefinition.field.organization'),
      cellDataType: 'object',
      field: 'groups',
      // 表示には使用しない(CSV出力用)
      valueFormatter: ({ value }) => getOrganizationText(value, ','),
      cellRenderer: 'OrganizationCellRenderer',
      comparator: (a, b) =>
        compareString(getOrganizationText(a), getOrganizationText(b)),
    },
    // ビル(フロア)
    {
      colId: COLUMN_ID.INDOOR,
      headerName: i18n.t('useUserListColumnDefinition.field.indoor'),
      cellDataType: 'text',
      valueGetter: ({ data }) =>
        useUserStatusAdditionalItemIndoor({
          levelName: data.indoor_level_name,
          levelShortName: data.indoor_level_short_name,
          buildingName: data.indoor_building_name,
          buildingShortName: data.indoor_building_short_name,
        }).text.value,
      cellRenderer: 'TextWithIFieldTooltipCellRenderer',
      comparator: compareString,
    },
    // プレイス
    {
      colId: COLUMN_ID.PLACE,
      headerName: i18n.t('useUserListColumnDefinition.field.place'),
      cellDataType: 'text',
      field: 'place_name',
      valueGetter: ({ data }) =>
        data.checkin_place_id && !data.checkout_at
          ? data.checkin_place_name
          : data.place_name,
      valueFormatter: ({ value }) =>
        useUserStatusAdditionalItemPlace({ placeName: value }).text.value,
      cellRenderer: 'TextWithIFieldTooltipCellRenderer',
      comparator: compareString,
    },
    // チェックイン状態
    {
      colId: COLUMN_ID.CHECKIN,
      headerName: i18n.t('useUserListColumnDefinition.field.checkin'),
      cellDataType: 'boolean',
      valueGetter: ({ data }) => data.checkin_place_id && !data.checkout_at,
      valueFormatter: ({ value }) =>
        value ? i18n.t('useUserListColumnDefinition.checkingIn') : '-',
      cellRenderer: 'TextWithIFieldTooltipCellRenderer',
    },
    // ステイタス
    {
      colId: COLUMN_ID.STATUS,
      headerName: i18n.t('useUserListColumnDefinition.field.status'),
      cellDataType: 'text',
      field: 'current_status_name',
      valueFormatter: ({ value }) =>
        useUserStatusAdditionalItemStatus({ statusName: value }).text.value,
      cellRenderer: 'TextWithIFieldTooltipCellRenderer',
      comparator: compareString,
    },
    // 稼働状態
    {
      colId: COLUMN_ID.ACTIVITY,
      headerName: i18n.t('useUserListColumnDefinition.field.activity'),
      cellDataType: 'text',
      field: 'activity',
      valueFormatter: ({ value }) =>
        useUserStatusAdditionalItemActivity({ value }).valueText.value,
      cellRenderer: 'TextWithIFieldTooltipCellRenderer',
      comparator: compareActivity,
      initialWidth: 112,
    },
    // 測位状態
    {
      colId: COLUMN_ID.POSITIONING,
      headerName: i18n.t('useUserListColumnDefinition.field.positioning'),
      cellDataType: 'boolean',
      valueGetter: ({ data, context }) => {
        const mLastPositioningAt = moment(data.last_positioning_at)
        return (
          mLastPositioningAt.isValid() &&
          mLastPositioningAt - context.positioningThresholdDate >= 0
        )
      },
      valueFormatter: ({ value }) =>
        value
          ? i18n.t('useUserListColumnDefinition.inPositioning')
          : i18n.t('useUserListColumnDefinition.notInPositioning'),
      cellRenderer: 'TextWithIFieldTooltipCellRenderer',
      initialWidth: 112,
    },
    // 測位の正確性
    {
      colId: COLUMN_ID.ACCURACY,
      headerName: i18n.t('useUserListColumnDefinition.field.accuracy'),
      cellDataType: 'number',
      field: 'accuracy',
      valueFormatter: ({ value }) =>
        useUserStatusAdditionalItemAccuracy({ accuracy: value }).accuracyText
          .value,
      type: 'numericColumn',
      cellRenderer: 'TextWithIFieldTooltipCellRenderer',
      comparator: compareNumber,
    },
    // 測位の正確性評価
    {
      colId: COLUMN_ID.ACCURACY_RATING,
      headerName: i18n.t('useUserListColumnDefinition.field.accuracyRating'),
      cellDataType: 'object',
      valueGetter: ({ data }) => ({
        rating: data.accuracy_rating,
        positioningStatus: data.positioning_status,
      }),
      valueFormatter: ({ value }) =>
        useUserStatusAdditionalItemAccuracy(value).ratingText.value,
      cellRenderer: 'TextWithIFieldTooltipCellRenderer',
      comparator: (a, b) => compareAccuracyRating(a.rating, b.rating),
    },
    // 測位日時
    {
      colId: COLUMN_ID.LAST_POSITIONING_AT,
      headerName: i18n.t('useUserListColumnDefinition.field.lastPositioningAt'),
      cellDataType: 'dateString',
      field: 'last_positioning_at',
      valueFormatter: ({ value }) =>
        useUserStatusAdditionalItemPositioning({ at: value }).formattedDate
          .value,
      cellRenderer: 'TextWithIFieldTooltipCellRenderer',
      comparator: compareDatetime,
    },
    // バッテリー
    {
      colId: COLUMN_ID.BATTERY,
      headerName: i18n.t('useUserListColumnDefinition.field.battery'),
      cellDataType: 'number',
      field: 'battery',
      valueFormatter: ({ value }) =>
        useUserStatusAdditionalItemBattery({ value }).text.value,
      type: 'numericColumn',
      cellRenderer: 'TextWithIFieldTooltipCellRenderer',
      comparator: compareNumber,
      initialWidth: 128,
    },
    // フリガナ
    {
      colId: COLUMN_ID.USER_NAME_KANA,
      headerName: i18n.t('useUserListColumnDefinition.field.userNameKana'),
      cellDataType: 'text',
      field: 'user_name_kana',
      cellRenderer: 'TextWithIFieldTooltipCellRenderer',
      comparator: compareString,
      initialHide: true,
    },
    // 権限
    {
      colId: COLUMN_ID.ROLE,
      headerName: i18n.t('useUserListColumnDefinition.field.role'),
      // 元の値は数値だがCSV出力時に数値として処理しないため別の cellDataType を設定
      cellDataType: 'role',
      field: 'role_id',
      valueFormatter: ({ value }) => {
        const roleName = store.getters['role/roleMap'].get(value)?.role_name
        return roleName ? i18n.t(`code.role.${roleName}`) : '-'
      },
      cellRenderer: 'TextWithIFieldTooltipCellRenderer',
      comparator: (a, b) => {
        const roleA = store.getters['role/roleMap'].get(a)?.role_name
        const roleB = store.getters['role/roleMap'].get(b)?.role_name
        return compareRole(roleA, roleB)
      },
      initialHide: true,
    },
    // 承認権限
    {
      colId: COLUMN_ID.MANAGER,
      headerName: i18n.t('useUserListColumnDefinition.field.manager'),
      cellDataType: 'boolean',
      field: 'manage_flg',
      valueFormatter: ({ value }) =>
        value === true ? i18n.t('useUserListColumnDefinition.has') : '-',
      cellRenderer: 'TextWithIFieldTooltipCellRenderer',
      initialHide: true,
    },
    // 電話番号
    {
      colId: COLUMN_ID.TEL,
      headerName: i18n.t('useUserListColumnDefinition.field.tel'),
      cellDataType: 'text',
      field: 'tel',
      cellRenderer: 'TextWithIFieldTooltipCellRenderer',
      comparator: (a, b) => {
        // 比較時にハイフンを取り除く
        const rawA = `${a || ''}`.replaceAll('-', '')
        const rawB = `${b || ''}`.replaceAll('-', '')
        return compareString(rawA, rawB)
      },
      initialHide: true,
    },
    // メールアドレス
    {
      colId: COLUMN_ID.EMAIL,
      headerName: i18n.t('useUserListColumnDefinition.field.email'),
      cellDataType: 'text',
      field: 'email',
      cellRenderer: 'TextWithIFieldTooltipCellRenderer',
      comparator: compareString,
      initialHide: true,
    },
    // メモ
    {
      colId: COLUMN_ID.REMARKS,
      headerName: i18n.t('useUserListColumnDefinition.field.remarks'),
      cellDataType: 'text',
      field: 'remarks',
      cellRenderer: 'TextWithIFieldTooltipCellRenderer',
      comparator: compareString,
      initialHide: true,
    },
    // 最終ログイン日時
    {
      colId: COLUMN_ID.LAST_LOGIN_AT,
      headerName: i18n.t('useUserListColumnDefinition.field.lastLoginAt'),
      cellDataType: 'dateString',
      field: 'last_login_at',
      valueFormatter: ({ value }) =>
        useDateDisplayText({ date: value, format: RECORD_DATE_FORMAT })
          .formattedDate.value || '-',
      cellRenderer: 'TextWithIFieldTooltipCellRenderer',
      comparator: compareDatetime,
      initialHide: true,
    },
    // 登録者
    {
      colId: COLUMN_ID.CREATE_USER_NAME,
      headerName: i18n.t('useUserListColumnDefinition.field.createUserName'),
      cellDataType: 'text',
      field: 'create_user_name',
      cellRenderer: 'TextWithIFieldTooltipCellRenderer',
      comparator: compareString,
      initialHide: true,
    },
    // 登録日時
    {
      colId: COLUMN_ID.CREATE_AT,
      headerName: i18n.t('useUserListColumnDefinition.field.createAt'),
      cellDataType: 'dateString',
      field: 'create_at',
      valueFormatter: ({ value }) =>
        useDateDisplayText({ date: value, format: RECORD_DATE_FORMAT })
          .formattedDate.value,
      cellRenderer: 'TextWithIFieldTooltipCellRenderer',
      comparator: compareDatetime,
      initialHide: true,
    },
    // 更新者
    {
      colId: COLUMN_ID.UPDATE_USER_NAME,
      headerName: i18n.t('useUserListColumnDefinition.field.updateUserName'),
      cellDataType: 'text',
      field: 'update_user_name',
      cellRenderer: 'TextWithIFieldTooltipCellRenderer',
      comparator: compareString,
      initialHide: true,
    },
    // 更新日時
    {
      colId: COLUMN_ID.UPDATE_AT,
      headerName: i18n.t('useUserListColumnDefinition.field.updateAt'),
      cellDataType: 'dateString',
      field: 'update_at',
      valueFormatter: ({ value }) =>
        useDateDisplayText({ date: value, format: RECORD_DATE_FORMAT })
          .formattedDate.value,
      cellRenderer: 'TextWithIFieldTooltipCellRenderer',
      comparator: compareDatetime,
      initialHide: true,
    },
  ])

  /**
   * 利用可能な固定カラムの定義
   * @type {ComputedRef.<ColDef[]>}
   */
  const availableFixedColumnDefs = computed(() =>
    fixedColumnDefs.value.filter((col) => {
      const functionTypes = COLUMN_ID_FUNCTION_TYPE_RELATION[col.colId]
      if (!functionTypes) return true
      return functionTypes.every(
        (type) => !!store.getters['functionType'](type)
      )
    })
  )

  /**
   * ユーザー属性マスタ
   * @type {ComputedRef.<Object[]>}
   */
  const userAttributes = computed(
    () => store.getters['userAttribute/userAttributes']
  )
  /**
   * ユーザー属性のカラム定義
   * @type {ComputedRef.<ColDef[]>}
   */
  const userAttributeColumnDefs = computed(() =>
    userAttributes.value.map((attr) => {
      return {
        colId: `attribute${attr.id}`,
        headerName: attr.attribute_name,
        cellDataType: ATTRIBUTE_CELL_DATA_TYPE[attr.attribute_type],
        valueGetter: ({ data }) => {
          const datum =
            data.attributes.find((a) => a.attribute_id === attr.id) || {}
          return {
            attributeType: datum.attribute_type,
            value: datum.attribute_value,
          }
        },
        valueFormatter: ({ value }) =>
          useUserStatusAdditionalItemUserAttribute(value).valueText.value,
        type:
          attr.attribute_type == ATTRIBUTE_TYPE.NUMBER
            ? 'numericColumn'
            : undefined,
        cellRenderer:
          attr.attribute_type == ATTRIBUTE_TYPE.LINK
            ? 'AttributeLinkCellRenderer'
            : 'TextWithIFieldTooltipCellRenderer',
        comparator: (a, b) =>
          attributeComparator[attr.attribute_type](a.value, b.value),
      }
    })
  )

  /**
   * カラム定義(全結合版)
   * @type {ComputedRef.<ColDef[]>}
   */
  const columnDefs = computed(() => [
    ...availableFixedColumnDefs.value,
    ...userAttributeColumnDefs.value,
  ])

  return {
    context,
    dataTypeDefinitions: DATA_TYPE_DEFINITIONS,
    defaultColumnDef: DEFAULT_COLUMN_DEF,
    columnDefs,
    autoWidthColumnsOnFirstRendered: AUTO_WIDTH_COLUMNS_ON_FIRST_RENDERED,
  }
}
